/**
 * SOLID Review
 * S — Good. One concern: the local player's moves — for each, what to send (the plan) and what the
 *     server's answer does to the table. Each pair is the click handler it replaces, split at its await.
 * O — Good. A new move is a new pair of functions here and two ops on the contract.
 * L — N/A — functions.
 * I — Good. Each decision takes the table and its own command.
 * D — Strong. The draft helpers, the dice rules and the schema types.
 */
import { GamePhase } from '../../uml-components/primitives';
import { MEYER_RANK } from '../../dice/DiceRankingService';
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { settle, freshLives, challengerOf, EMPTY_TABLE, type IConfigSettle } from './tableDraft';
import type { IPropsClientTableInput, IStateClientTable } from '../../ports/schemas/ClientTable';
import type { IPropsClientChanges } from '../../ports/schemas/ClientChanges';
import type { IPropsClientSessionChanges } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type {
  ICommandNothing, ICommandAnnounce, ICommandLeave, ICommandGameStarted, ICommandRoundMoveAnswered, ICommandPeeked,
  ICommandClaimAccepted, ICommandBelieveAccepted, ICommandPlayAgainAnswered,
  IPayloadStartGame, IPayloadRoundMove, IPayloadAnnounce, IPayloadBelieve, IPayloadPlayAgain, IEntityLeave,
} from '../../ports/schemas/TableMoves';

/**
 * THE LOCAL PLAYER'S MOVES (frontend strict-ports plan, FE1, 2026-10-03), from LobbyPanel's Start,
 * GameSceneCore's cup tap and ControlPanel's buttons. Each PLAN replays its handler's opening guard: where
 * the handler returned silently, the plan refuses with a code. It then says what to send. Each ANSWERED
 * decision replays what the handler did once the call came back.
 *
 * One guard is new and the same everywhere: an answer that arrives after the player has left the table is
 * ignored. The handlers wrote it into whatever the store held by then, which after `resetAll` was a table
 * the player had already left (deviation, flagged in §28). No answer is checked against the round it was
 * sent for. The server's round-start event may arrive first and move the round on, and the answer must
 * still apply exactly as the handler applied it.
 */

type TPlan<R> = TDecisionResult<IEntityDecision<IPropsClientChanges, R>>;
type TAnswered = TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityTableAck>>;

const NOTHING: IPropsClientChanges = {};

function planned<R>(request: R, changes: IPropsClientChanges = NOTHING): TPlan<R> {
  return decided(changes, request);
}

function answered(base: IStateClientTable, next: IStateClientTable, markActivity: boolean): TAnswered {
  const dto: IEntityTableAck = { applied: true };
  const how: IConfigSettle = { markActivity };
  return decided(settle(base, next, how), dto);
}

function ignored(): TAnswered {
  const dto: IEntityTableAck = { applied: false };
  return decided(NOTHING, dto);
}

/** Still at a table, as the player who sent the request. */
function seatedAs(table: IStateClientTable, playerId: string): boolean {
  return table.room !== null && table.localPlayerId === playerId;
}

function notReady<R>(what: string): TPlan<R> {
  return refuse(`The table is not ready: ${what}.`, REFUSAL.NOT_READY);
}

// ── Start (LobbyPanel) ──────────────────────────────────────────────────────────────────────────

export function decidePlanStartGame(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadStartGame> {
  const { table } = input;
  if (table.room === null || table.localPlayerId === null) return notReady('no room or no identity');
  const request: IPayloadStartGame = { roomId: table.room.roomId, playerId: table.localPlayerId };
  return planned(request);
}

/**
 * The host rolls first and moves to ROLLING on the answer, without waiting for the round's start event:
 * without the round id the cup tap bailed out, the "sometimes can't roll on game start" race.
 */
export function decideGameStarted(input: IPropsClientTableInput, command: ICommandGameStarted): TAnswered {
  const { table } = input;
  const { request, answer } = command;
  if (!seatedAs(table, request.playerId) || table.room?.roomId !== request.roomId) return ignored();
  const next: IStateClientTable = { ...table };
  if (answer.roundId) next.activeRoundId = answer.roundId;
  next.activeRollerId = answer.rollerId ?? request.playerId;
  next.phase = GamePhase.ROLLING;
  return answered(table, next, false);
}

// ── The cup (GameSceneCore) ─────────────────────────────────────────────────────────────────────

function cupMove(table: IStateClientTable, phase: GamePhase, needsHiddenDice: boolean): TPlan<IPayloadRoundMove> {
  const me = table.localPlayerId;
  if (me === null || table.activeRoundId === null) return notReady('no round or no identity');
  if (me !== table.activeRollerId) return refuse('Only the roller may touch the cup.', REFUSAL.NOT_YOUR_MOVE);
  if (table.phase !== phase || (needsHiddenDice && table.diceRevealed)) {
    return refuse(`The cup does nothing in ${table.phase}${table.diceRevealed ? ' with the dice seen' : ''}.`, REFUSAL.NOT_YOUR_MOVE);
  }
  const request: IPayloadRoundMove = { roundId: table.activeRoundId, playerId: me };
  return planned(request);
}

/** First tap, in ROLLING: roll. The server keeps the dice secret until the roller peeks. */
export function decidePlanRoll(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadRoundMove> {
  return cupMove(input.table, GamePhase.ROLLING, false);
}

export function decideRolled(input: IPropsClientTableInput, command: ICommandRoundMoveAnswered): TAnswered {
  const { table } = input;
  if (!seatedAs(table, command.request.playerId)) return ignored();
  const next: IStateClientTable = { ...table, diceRevealed: false, phase: GamePhase.PEEKING };
  return answered(table, next, true);
}

/** Second tap, in PEEKING with the dice still hidden: ask the server for them. */
export function decidePlanPeek(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadRoundMove> {
  return cupMove(input.table, GamePhase.PEEKING, true);
}

export function decidePeeked(input: IPropsClientTableInput, command: ICommandPeeked): TAnswered {
  const { table } = input;
  if (!seatedAs(table, command.request.playerId)) return ignored();
  const next: IStateClientTable = { ...table, lastDiceRoll: command.answer, diceRevealed: true };
  return answered(table, next, true);
}

// ── Claim (ControlPanel) ────────────────────────────────────────────────────────────────────────

/**
 * Two endpoints for one rule, chosen by whether these dice have been seen here: an open claim, or a blind
 * pass. The server owns the verdict either way — it refuses a blind pass once it has recorded a peek, so a
 * client that lost track of its own state is told rather than silently downgraded (§28.62).
 */
export function decidePlanAnnounce(input: IPropsClientTableInput, command: ICommandAnnounce): TPlan<IPayloadAnnounce> {
  const { table } = input;
  if (table.activeRoundId === null || table.localPlayerId === null) return notReady('no round or no identity');
  const request: IPayloadAnnounce = {
    roundId: table.activeRoundId,
    playerId: table.localPlayerId,
    score: command.score,
    blind: !table.diceRevealed,
  };
  return planned(request);
}

/** The claim stands: the server's decider, the score, whether it went blind; everyone now decides. */
export function decideClaimAccepted(input: IPropsClientTableInput, command: ICommandClaimAccepted): TAnswered {
  const { table } = input;
  const { request, answer } = command;
  if (!seatedAs(table, request.playerId)) return ignored();
  const next: IStateClientTable = { ...table };
  next.activeChallengerId = challengerOf(answer.challengerId);
  next.lastClaimedScore = request.score;
  next.claimWasBlind = answer.wasBlind ?? request.blind;
  next.phase = GamePhase.DECIDING;
  return answered(table, next, true);
}

// ── Decide (ControlPanel) ───────────────────────────────────────────────────────────────────────

/** Believe: the next roller must beat the believed score. Meyer cannot be believed, only lifted. */
export function decidePlanBelieve(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadBelieve> {
  const { table } = input;
  if (table.activeRoundId === null || table.localPlayerId === null || table.room === null) return notReady('no round, room or identity');
  if (table.opponentClaim?.score === MEYER_RANK) return refuse('Meyer cannot be believed — only lifted.', REFUSAL.MEYER_CANNOT_BE_BELIEVED);
  const request: IPayloadBelieve = {
    roundId: table.activeRoundId,
    playerId: table.localPlayerId,
    roomId: table.room.roomId,
    minimumScore: table.opponentClaim?.score ?? 0,
  };
  return planned(request);
}

export function decideBelieveAccepted(input: IPropsClientTableInput, command: ICommandBelieveAccepted): TAnswered {
  const { table } = input;
  const { request, answer } = command;
  if (!seatedAs(table, request.playerId) || table.room?.roomId !== request.roomId) return ignored();
  const next: IStateClientTable = { ...table };
  next.activeRoundId = answer.roundId ?? null;
  next.activeRollerId = answer.rollerId ?? null;
  next.minimumClaimScore = answer.minimumScore ?? request.minimumScore;
  next.lastDiceRoll = null;
  next.opponentClaim = null;
  next.activeChallengerId = null;
  next.phase = GamePhase.ROLLING;
  return answered(table, next, true);
}

/** Roll First: the loser opens the next round with a clean floor (0 = any valid claim). */
export function decidePlanRollFirst(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadBelieve> {
  const { table } = input;
  if (table.activeRoundId === null || table.localPlayerId === null || table.room === null) return notReady('no round, room or identity');
  const request: IPayloadBelieve = { roundId: table.activeRoundId, playerId: table.localPlayerId, roomId: table.room.roomId, minimumScore: 0 };
  return planned(request);
}

/** Like a believe, but with no floor and without clearing the server's decider — the handler did not. */
export function decideRollFirstAccepted(input: IPropsClientTableInput, command: ICommandBelieveAccepted): TAnswered {
  const { table } = input;
  const { request, answer } = command;
  if (!seatedAs(table, request.playerId) || table.room?.roomId !== request.roomId) return ignored();
  const next: IStateClientTable = { ...table };
  next.activeRoundId = answer.roundId ?? null;
  next.activeRollerId = answer.rollerId ?? null;
  next.minimumClaimScore = 0;
  next.lastDiceRoll = null;
  next.opponentClaim = null;
  next.phase = GamePhase.ROLLING;
  return answered(table, next, true);
}

/** Lift: the server's decider is cleared BEFORE the call; the reveal arrives as the cup-lift event. */
export function decidePlanLift(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadRoundMove> {
  const { table } = input;
  if (table.activeRoundId === null || table.localPlayerId === null) return notReady('no round or no identity');
  const request: IPayloadRoundMove = { roundId: table.activeRoundId, playerId: table.localPlayerId };
  const next: IStateClientTable = { ...table, activeChallengerId: null };
  return planned(request, settle(table, next));
}

/** The lift came back — answered or refused, the table moved. */
export function decideLiftAnswered(input: IPropsClientTableInput, command: ICommandRoundMoveAnswered): TAnswered {
  const { table } = input;
  if (!seatedAs(table, command.request.playerId)) return ignored();
  return answered(table, table, true);
}

// ── After the game (ControlPanel) ───────────────────────────────────────────────────────────────

/** Play Again: every seat back to full lives BEFORE the call; the last loser rolls first. */
export function decidePlanPlayAgain(input: IPropsClientTableInput, _command: ICommandNothing): TPlan<IPayloadPlayAgain> {
  const { table } = input;
  if (table.room === null || table.revealResult === null) return notReady('no room or no reveal');
  const request: IPayloadPlayAgain = { roomId: table.room.roomId, rollerId: table.revealResult.losingPlayerId };
  const next: IStateClientTable = { ...table, playerLives: freshLives(table.room.players.map((p) => p.id)) };
  return planned(request, settle(table, next));
}

/**
 * The table moved either way. If the server opened the round and the local player is the new roller,
 * they move to ROLLING on the answer instead of waiting for the round's start event — otherwise the round
 * id can be missing when they tap the cup, and the roll does nothing.
 */
export function decidePlayAgainAnswered(input: IPropsClientTableInput, command: ICommandPlayAgainAnswered): TAnswered {
  const { table } = input;
  const { request, answer } = command;
  if (table.room === null || table.room.roomId !== request.roomId) return ignored();
  const next: IStateClientTable = { ...table };
  if (answer !== null && answer.roundId && table.localPlayerId === request.rollerId) {
    next.activeRoundId = answer.roundId;
    next.activeRollerId = answer.rollerId ?? null;
    next.minimumClaimScore = 0;
    next.lastDiceRoll = null;
    next.phase = GamePhase.ROLLING;
  }
  return answered(table, next, true);
}

/**
 * Leave the table: everything back to the store's initial state — `resetAll`, which also forgets the
 * identity until the identity flows restore it. The server is told only from the exit button. The
 * room-ended overlay's "back to menu" has nobody to tell: the server closed the table.
 */
export function decideLeaveTable(input: IPropsClientTableInput, command: ICommandLeave): TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityLeave>> {
  const { table } = input;
  const seat = command.notifyServer && table.room !== null && table.localPlayerId !== null
    ? { roomId: table.room.roomId, playerId: table.localPlayerId }
    : null;
  const dto: IEntityLeave = { leave: seat };
  const next: IStateClientTable = { ...EMPTY_TABLE };
  const how: IConfigSettle = { reset: true };
  // FE2: what every caller did after the reset — ControlPanel's back-to-menu, the room-ended overlay and
  // the leave-waiting overlay each forgot the stored game and went back to the welcome screen, and the
  // overlays dropped "restoring". One decision now, so no caller can do half of it.
  const changes = settle(table, next, how);
  const session: IPropsClientSessionChanges = { screen: 'WELCOME', restoring: false };
  changes.session = session;
  changes.persist = { ...changes.persist, resume: null };
  return decided(changes, dto);
}
